import {
  FlexBox,
  JoonUIColor,
  QuestStatus,
  QuestType,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useShallow } from "zustand/react/shallow"

import CatchUpCompleteEmptyState from "./CatchUpCompleteEmptyState"
import CatchUpTabBar from "./CatchUpTabBar"
import CatchUpCompleteActionButtons from "./CatchUpTabBarCompleteButtons"
import { useCatchUpCompleteStore } from "./useCatchUpCompleteStore"
import Card from "../../components/card/Card"
import { useQuestBoardQuery, useUserQuery } from "../../networking/queries"
import { isCoachQuest } from "../../util/util"
import CareTeamAvatar from "../care/CareTeamAvatar"
import useCareTeam from "../care/useCareTeam"
import { ROUTINE_LABEL_KEYS } from "../questBoard/addEditQuestModal/questLabelKeys"

const CatchUpCompleteContent = () => {
  const {
    selectedUserProfile,
    selectedQuestIdsForCompletion,
    toggleQuestIdForCompletion,
  } = useCatchUpCompleteStore(
    useShallow((state) => ({
      selectedUserProfile: state.selectedUserProfile,
      selectedQuestIdsForCompletion: state.selectedQuestIdsForCompletion,
      toggleQuestIdForCompletion: state.toggleQuestIdForCompletion,
    }))
  )

  const { user } = useUserQuery()
  const { careTeam } = useCareTeam()
  const { data: quests } = useQuestBoardQuery()
  const { t } = useTranslation()

  const questsToComplete = quests?.filter(
    (quest) =>
      [QuestStatus.OPEN, QuestStatus.RETRY].includes(quest.status) &&
      quest.user_id === selectedUserProfile?.user.id
  )

  if (!questsToComplete || questsToComplete.length === 0)
    return <CatchUpCompleteEmptyState />

  const isSelfSelected = selectedUserProfile?.user.id === user?.id

  return (
    <>
      <FlexBox
        style={{
          padding: `${SPACING.space4} ${SPACING.space6}`,
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.80) 2.83%, rgba(255, 255, 255, 0.00) 51.53%, rgba(255, 255, 255, 0.80) 99.81%), var(--surface-background-light-accent, #E9EAFA)",
          borderTopLeftRadius: SPACING.space4,
          borderTopRightRadius: SPACING.space4,
          marginBottom: "auto",
        }}
        gap={SPACING.space4}
      >
        <Typography variant="bodySmall">
          {isSelfSelected
            ? t("CATCHUP_REMAINING_SELF")
            : t("CATCHUP_REMAINING_OTHER", {
                name: selectedUserProfile?.user.name,
              })}
        </Typography>
        <FlexBox direction="column" gap={SPACING.space1}>
          {questsToComplete?.map((quest) => {
            const careTeamProfile = careTeam?.find(
              (profile) =>
                profile.user.id === quest.series.assigner_profile?.user_id
            )
            return (
              <div
                style={{ position: "relative", width: "100%" }}
                key={quest.id}
              >
                {/* @ts-ignore */}
                {quest.temp_status && (
                  // @ts-ignore
                  <QuestCardStatusOverlay questStatus={quest.temp_status} />
                )}
                <Card
                  title={
                    <div className="w-full flex flex-row items-center gap-1">
                      {isCoachQuest(quest) &&
                        careTeamProfile?.profile_image_url && (
                          <CareTeamAvatar profile={careTeamProfile} size="xs" />
                        )}
                      <Typography variant="bodySmallBold">
                        {quest.series.title}
                      </Typography>
                    </div>
                  }
                  subtitle={
                    (
                      <div>
                        {quest.series.photo_auto_verify_max &&
                          quest.photo_auto_verify_count &&
                          quest.photo_auto_verify_count > 0 && (
                            <Typography
                              variant="bodySmall"
                              textAlign="left"
                              color={JoonUIColor.semantic.alert}
                            >
                              {t("CATCHUP_REJECTED_BY_AI")}
                            </Typography>
                          )}
                        <div>{`${t(ROUTINE_LABEL_KEYS[quest.series.routine])}${quest.series.reminder_time ? `, ${quest.series.reminder_time}` : ""}${quest.series.type === QuestType.REPEATING ? ` ${t("CATCHUP_REPEATING")}` : ""}`}</div>
                      </div>
                    ) as any
                  }
                  style={{ borderRadius: SPACING.space4 }}
                  isChecked={selectedQuestIdsForCompletion.includes(quest.id)}
                  coinAmount={quest.series.redeemable_reward}
                  onClick={() => toggleQuestIdForCompletion(quest.id)}
                />
              </div>
            )
          })}
        </FlexBox>
      </FlexBox>
      <CatchUpTabBar>
        <CatchUpCompleteActionButtons />
      </CatchUpTabBar>
    </>
  )
}

export const QuestCardStatusOverlay = ({
  questStatus,
}: {
  questStatus: QuestStatus
}) => {
  const { t } = useTranslation()
  const overlayStyles = useMemo(() => {
    switch (questStatus) {
      case QuestStatus.REJECTED:
        return {
          background: "rgba(255, 233, 233, 0.80)",
          primaryColor: JoonUIColor.semantic.alert,
          text: t("QB_TAB_REJECTED"),
        }
      case QuestStatus.VERIFIED:
        return {
          background: "rgba(220, 252, 244, 0.80)",
          primaryColor: JoonUIColor.semantic.success,
          text: t("QB_TAB_COMPLETED"),
        }
      case QuestStatus.SKIPPED:
        return {
          background: "rgba(233, 233, 255, 0.80)",
          primaryColor: JoonUIColor.text.secondary,
          text: t("QB_TAB_SKIPPED"),
        }
      case QuestStatus.RETRY:
        return {
          background: "rgba(233, 233, 255, 0.80)",
          primaryColor: JoonUIColor.text.secondary,
          text: t("CATCHUP_STATUS_RETRY"),
        }
      default:
        return {}
    }
  }, [questStatus, t])

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
        top: "0",
        left: "0",
        bottom: "0",
        zIndex: 100,
        right: "0",
        border: `2px solid ${overlayStyles.primaryColor}`,
        borderRadius: SPACING.space2,
        background: overlayStyles.background,
      }}
    >
      <Typography variant="h3" color={overlayStyles.primaryColor}>
        {overlayStyles.text}
      </Typography>
    </div>
  )
}

export default CatchUpCompleteContent
